import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import ByClientMonthAlbumsTable from '../components/ByClientMonthAlbumsTable';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import Breadcrumb from '../components/Breadcrumb';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';

function ByClientMonthAlbumsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const clientId = params.get('clientId');
  const calculateMonth = params.get('calculateMonth');
  const [loading, setLoading] = useState(false);
  const [byClientMonthAlbums, setByClientMonthAlbums] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [month, setMonth] = useState('');
  const [clientName, setClientName] = useState('권리사');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 15;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/ByClient/ByClientMonthAlbums`,
        {
          params: {
            clientId,
            calculateMonth,
            page: currentPage,
            limit: pageSize,
            search: searchKeyword,
          },
        }
      );
      // setByClientMonthAlbums(
      //   (response.data.items || []).map((item) => ({
      //     ...item,
      //     clientId: clientId, // 명시적으로 clientId 추가
      //   }))
      // );
      setByClientMonthAlbums(response.data.items || []);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
      const found = response.data.items.find(
        (a) => a.calculateMonth === calculateMonth
      );
      if (found) {
        setClientName(found.album?.albumClients?.client?.name ?? '권리사');
      }
    } catch (error) {
      console.error('서버 접속 에러:', error);
      setByClientMonthAlbums([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [calculateMonth, currentPage, searchKeyword, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const monthList = useMemo(() => {
    return Array.from(
      new Set(
        byClientMonthAlbums.map((album) => album.calculateMonth.slice(0, 7))
      )
    )
      .sort()
      .reverse();
  }, [byClientMonthAlbums]);

  useEffect(() => {
    if (monthList.length > 0 && !month) {
      setMonth(monthList[0]);
    }
  }, [monthList, month]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const filteredAlbums = useMemo(() => {
    return byClientMonthAlbums
      .filter((album) => album.calculateMonth.slice(0, 7) === month)
      .filter((album) => album.album.nameKr.includes(searchKeyword))
      .sort((a, b) => b.clientAmount - a.clientAmount);
  }, [byClientMonthAlbums, month, searchKeyword]);

  const showTitle = month !== '';

  const formatMonth = (monthStr, clientName, me) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');

    if (me?.adminYn === 'Y') {
      return `${year}년 ${parseInt(month, 10)}월 ${clientName} 앨범별 정산`;
    } else {
      return `${year}년 ${parseInt(month, 10)}월 앨범별 정산`;
    }
  };

  const isAdmin = me?.adminYn === 'Y';

  const breadcrumbItems = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth' },
    { label: '앨범별 정산' },
  ];

  return (
    <>
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex mb-8 justify-center items-center">
          {showTitle && (
            <div className="text-xl sm:text-2xl font-bold text-center">
              {formatMonth(month, clientName ?? '권리사', me ?? {})}
            </div>
          )}
        </div>

        <SearchInput
          placeholder="앨범명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1);
            setSearchKeyword(searchTerm);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            <ByClientMonthAlbumsTable
              data={filteredAlbums}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              clientId={clientId}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
    </>
  );
}

export default ByClientMonthAlbumsPage;
